import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Clock, Info, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import Timeline from '../../components/Timeline';

const todayStr = () => {
  const d = new Date();
  return d.toISOString().split('T')[0];
};

// Extrai HH:mm de um datetime ISO ("2026-07-28T08:00:00-03:00") ou de uma string "HH:MM"
const toHHmm = (value) => {
  if (!value) return '';
  if (value.includes('T')) return value.slice(11, 16);
  return value.slice(0, 5);
};

const TimeEntryForm = ({ mode: propMode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isStaff } = useAuth();
  const mode = propMode || (id ? 'edit' : 'create');
  const isCopy = mode === 'copy';
  const isEditing = mode === 'edit';

  const [formData, setFormData] = useState({
    consultant: user?.id || '',
    client: '',
    project: '',
    entry_type: 'daily',
    entry_date: todayStr(),
    attendance_type: 'remote',
    service_status: 'in_progress',
    title: '',
    description: '',
    periods: [{ start_time: '', end_time: '' }],
    target_date: todayStr(),
  });

  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entryOwnerId, setEntryOwnerId] = useState(null);
  const [entryOwnerName, setEntryOwnerName] = useState('');
  const [editEnabled, setEditEnabled] = useState(false);

  useEffect(() => {
    fetchClients();
    if (isStaff) fetchConsultants();
    if (id) fetchEntry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);


  useEffect(() => {
    // Apenas no modo de criação o consultor padrão é o usuário logado;
    // em edição/cópia o valor vem do lançamento carregado.
    if (mode === 'create') {
      setFormData((prev) => ({ ...prev, consultant: user?.id || '' }));
    }
  }, [user, mode]);

  // Gestor visualizando lançamento de outro consultor: inicia em somente leitura
  const managerViewingOther = Boolean(
    isEditing && isStaff && user && entryOwnerId && entryOwnerId !== user.id
  );
  const readOnly = managerViewingOther && !editEnabled;

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients/');
      setClients(response.data.results || response.data);
    } catch (err) {
      console.error('Erro ao carregar clientes', err);
    }
  };

  const fetchConsultants = async () => {
    try {
      const response = await api.get('/users/?role=consultor');
      setConsultants(response.data.results || response.data);
    } catch (err) {
      console.error('Erro ao carregar consultores', err);
    }
  };

  const fetchEntry = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/time-entries/${id}/`);
      const e = response.data;

      const periods = e.periods?.map((p) => ({
        start_time: toHHmm(p.start_time),
        end_time: toHHmm(p.end_time),
      })) || [{ start_time: '', end_time: '' }];

      const consultantId = typeof e.consultant === 'object' && e.consultant !== null
        ? e.consultant.id
        : e.consultant;
      const consultantName = typeof e.consultant === 'object' && e.consultant !== null
        ? (e.consultant.name || '')
        : (e.consultant_name || '');

      setEntryOwnerId(consultantId || null);
      setEntryOwnerName(consultantName);

      setFormData({
        consultant: consultantId || user?.id || '',
        client: e.client || '',
        project: e.project || '',
        entry_type: e.entry_type || 'daily',
        entry_date: e.entry_date || todayStr(),
        attendance_type: e.attendance_type || 'remote',
        service_status: e.service_status || 'in_progress',
        title: e.title || '',
        description: e.description || '',
        periods,
        target_date: todayStr(),
      });
      if (e.client) fetchProjectsByClient(e.client);
    } catch (err) {
      toast.error('Erro ao carregar lançamento.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectsByClient = async (clientId) => {
    try {
      const response = await api.get(`/projects/?client=${clientId}`);
      setProjects(response.data.results || response.data);
    } catch (err) {
      console.error('Erro ao carregar projetos', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'client') {
      setFormData((prev) => ({ ...prev, client: value, project: '' }));
      fetchProjectsByClient(value);
    }
  };

  const handlePeriodChange = (idx, field, value) => {
    const periods = [...formData.periods];
    periods[idx][field] = value;
    setFormData({ ...formData, periods });
  };

  const addPeriod = () => {
    setFormData({
      ...formData,
      periods: [...formData.periods, { start_time: '', end_time: '' }],
    });
  };

  const removePeriod = (idx) => {
    const periods = formData.periods.filter((_, i) => i !== idx);
    setFormData({ ...formData, periods: periods.length ? periods : [{ start_time: '', end_time: '' }] });
  };

  const buildPayload = () => {
    const date = isCopy ? formData.target_date : formData.entry_date;
    const localOffset = '-03:00';

    const periods = formData.periods
      .filter((p) => p.start_time && p.end_time)
      .map((p) => ({
        start_time: `${date}T${p.start_time}:00${localOffset}`,
        end_time: `${date}T${p.end_time}:00${localOffset}`,
      }));

    return {
      consultant: formData.consultant,
      client: formData.client,
      project: formData.project,
      entry_type: formData.entry_type,
      entry_date: date,
      attendance_type: formData.attendance_type,
      service_status: formData.service_status,
      title: formData.title,
      description: formData.description,
      periods,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;

    if (!formData.consultant || !formData.client || !formData.project || !formData.title) {
      toast.error('Preencha todos os campos obrigatórios.');
      return;
    }

    const payload = buildPayload();
    if (payload.periods.length === 0) {
      toast.error('Informe pelo menos um período válido.');
      return;
    }

    try {
      setSaving(true);
      if (isEditing) {
        await api.patch(`/time-entries/${id}/`, payload);
        toast.success('Lançamento atualizado com sucesso!');
      } else if (isCopy) {
        await api.post(`/time-entries/${id}/copy/`, { target_date: formData.target_date });
        toast.success('Lançamento copiado com sucesso!');
      } else {
        await api.post('/time-entries/', payload);
        toast.success('Lançamento criado com sucesso!');
      }
      navigate('/time-entries');
    } catch (err) {
      const messages = err.response?.data;
      const text = messages && typeof messages === 'object'
        ? Object.values(messages).flat()[0]
        : 'Erro ao salvar lançamento.';
      toast.error(text);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const getPageTitle = () => {
    if (isCopy) return 'Copiar Lançamento';
    if (isEditing) return 'Editar Lançamento';
    return 'Novo Lançamento';
  };

  const inputClass = readOnly
    ? 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-100 text-gray-600 outline-none'
    : 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none';
  const periodInputClass = readOnly
    ? 'px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-100 text-gray-600 outline-none'
    : 'px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none';

  return (
    <div className={`${isEditing ? 'max-w-7xl' : 'max-w-3xl'} mx-auto`}>
      <div className={isEditing ? 'grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-6 items-start' : 'space-y-6'}>
        <div className="space-y-6">
          <div>
            <button
              onClick={() => navigate('/time-entries')}
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft size={16} />
          Voltar
        </button>

        {readOnly && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-2 flex-1">
              <Info size={18} className="text-blue-600 mt-0.5 shrink-0" />
              <p className="text-sm text-blue-800">
                Você está visualizando um lançamento de <strong>{entryOwnerName || 'outro consultor'}</strong>.
                Clique em "Editar lançamento" para fazer alterações — elas ficarão registradas no histórico.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setEditEnabled(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shrink-0"
            >
              <Pencil size={16} />
              Editar lançamento
            </button>
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-6">{getPageTitle()}</h3>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Consultor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Consultor</label>
              {isStaff ? (
                <select
                  name="consultant"
                  required
                  value={formData.consultant}
                  onChange={handleChange}
                  disabled={readOnly}
                  className={inputClass}
                >
                  <option value="">Selecione</option>
                  {consultants.map((c) => (
                    <option key={c.id} value={c.id}>{c.first_name || c.username}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  readOnly
                  value={user?.first_name ? `${user.first_name} ${user.last_name}`.trim() : user?.username}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-100 text-gray-600"
                />
              )}
            </div>

            {/* Cliente e Projeto */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cliente *</label>
                <select
                  name="client"
                  required
                  value={formData.client}
                  onChange={handleChange}
                  disabled={readOnly}
                  className={inputClass}
                >
                  <option value="">Selecione</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Projeto *</label>
                <select
                  name="project"
                  required
                  value={formData.project}
                  onChange={handleChange}
                  disabled={readOnly}
                  className={inputClass}
                >
                  <option value="">Selecione</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tipo e Data */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Lançamento</label>
                <select
                  name="entry_type"
                  value={formData.entry_type}
                  onChange={handleChange}
                  disabled={readOnly}
                  className={inputClass}
                >
                  <option value="daily">Diário</option>
                  <option value="weekly">Semanal</option>
                  <option value="monthly">Mensal</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isCopy ? 'Data de Destino' : 'Data do Lançamento'} *
                </label>
                <input
                  type="date"
                  name={isCopy ? 'target_date' : 'entry_date'}
                  required
                  value={isCopy ? formData.target_date : formData.entry_date}
                  onChange={handleChange}
                  disabled={readOnly}
                  className={inputClass}
                />
              </div>
            </div>

            {/* Atendimento e Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Atendimento</label>
                <select
                  name="attendance_type"
                  value={formData.attendance_type}
                  onChange={handleChange}
                  disabled={readOnly}
                  className={inputClass}
                >
                  <option value="remote">Remoto</option>
                  <option value="in_person">Presencial</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status do Atendimento</label>
                <select
                  name="service_status"
                  value={formData.service_status}
                  onChange={handleChange}
                  disabled={readOnly}
                  className={inputClass}
                >
                  <option value="in_progress">Em andamento</option>
                  <option value="finished">Finalizado</option>
                  <option value="awaiting_client">Aguardando validação do cliente</option>
                </select>
              </div>
            </div>

            {/* Título e Descrição */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={handleChange}
                disabled={readOnly}
                className={inputClass}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
              <textarea
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
                disabled={readOnly}
                className={inputClass}
              />
            </div>

            {/* Períodos */}
            {!isCopy && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Períodos de Atuação</label>
                <div className="space-y-3">
                  {formData.periods.map((period, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <Clock size={16} className="text-gray-400" />
                      <input
                        type="time"
                        value={period.start_time}
                        onChange={(e) => handlePeriodChange(idx, 'start_time', e.target.value)}
                        disabled={readOnly}
                        className={periodInputClass}
                      />
                      <span className="text-gray-400">até</span>
                      <input
                        type="time"
                        value={period.end_time}
                        onChange={(e) => handlePeriodChange(idx, 'end_time', e.target.value)}
                        disabled={readOnly}
                        className={periodInputClass}
                      />
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => removePeriod(idx)}
                          className="p-2 text-red-500 hover:text-red-700"
                          title="Remover período"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={addPeriod}
                    className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    <Plus size={16} />
                    Adicionar período
                  </button>
                )}
              </div>
            )}

            {isCopy && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                A cópia criará um novo lançamento com os mesmos dados e períodos ajustados para a data de destino.
              </div>
            )}

            <div className="pt-4 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => navigate('/time-entries')}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {readOnly ? 'Voltar' : 'Cancelar'}
              </button>
              {!readOnly && (
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-70"
                >
                  <Save size={16} />
                  {saving ? 'Salvando...' : isCopy ? 'Copiar' : 'Salvar'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
        </div>

        {isEditing && (
          <div className="xl:sticky xl:top-6">
            <Timeline entryId={id} />
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeEntryForm;
